import { CloseOutlined } from '@ant-design/icons';
import { useNavigate } from '@tanstack/react-router';
import { ActionButton, BreadcrumbItem } from '@zeus/core/layout/MainLayout';
import PageContent from '@zeus/core/layout/PageContent';
import Loader from '@zeus/core/Loader';
import VersionPreviewModal from '@zeus/core/modals/VersionPreviewModal';
import { readmeHistoryAdapter } from '_/common/adapters/readmeHistory.adapter';
import ReadmeHistory from '_/common/components/readme/ReadmeHistory';
import { useCollection } from '_/contexts/CollectionContext';
import { useTopHeader } from '_/contexts/LayoutContext';
import { useOrganization } from '_/contexts/OrganizationContext';
import { useProject } from '_/contexts/ProjectContext';
import { useCollectionReadmeVersions, useUpdateCollection } from '_/hooks/useCollections';
import React, { useState, ReactElement, useMemo, useEffect, useCallback } from 'react';

interface Version {
  after: {
    content: string;
  };
}

function CollectionReadmeHistory(): ReactElement {
  const history = useNavigate();
  const { selectedCollection: collection, isLoading: isLoadingCollection, refetch: refetchCollection } = useCollection();
  const { selectedProject, selectedProjectSlug } = useProject();
  const { selectedOrganization, selectedOrgSlug } = useOrganization();
  const { setActionButtons, setBreadcrumbs } = useTopHeader();

  // React Query hooks
  const { data: rawReadmeVersions = [], isLoading: isLoadingVersions } = useCollectionReadmeVersions(collection?.id);
  const { mutateAsync: updateCollectionMutation } = useUpdateCollection();

  // Adapt versions data to ReadmeHistory format
  const readmeVersions = useMemo(() => readmeHistoryAdapter(rawReadmeVersions), [rawReadmeVersions]);

  const [selectedVersion, setSelectedVersion] = useState<Version | Record<string, never>>({});
  const [previewModalVisible, setPreviewModalVisible] = useState(false);

  const restoreToPreviousVersion = async (version: Version) => {
    if (!collection) return;

    const restoredReadme = version.after.content;
    await updateCollectionMutation({
      collectionId: collection.id,
      collectionReadme: restoredReadme,
    });

    await refetchCollection();
    void history({
      to: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${collection.slug}`,
      replace: true,
    });

    if (previewModalVisible) {
      setPreviewModalVisible(false);
      setSelectedVersion({});
    }
  };

  const previewPreviousVersion = (version?: Version) => {
    if (version) {
      setSelectedVersion(version);
    }
    setPreviewModalVisible(!previewModalVisible);
  };

  const handleCancelPreview = () => {
    setPreviewModalVisible(false);
    setSelectedVersion({});
  };

  const handleReturnToCollection = useCallback(() => {
    if (collection) {
      void history({
        to: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${collection.slug}`,
        replace: true,
      });
    }
  }, [collection, history, selectedOrgSlug, selectedProjectSlug]);

  const exitButton = useMemo<ActionButton>(() => {
    return {
      onClick: handleReturnToCollection,
      icon: <CloseOutlined />,
      tooltip: 'Return to Collection',
      label: 'Close',
    };
  }, [handleReturnToCollection]);

  // Set up breadcrumbs and action buttons
  useEffect(() => {
    if (collection) {
      const breadcrumbs: BreadcrumbItem[] = [
        { title: 'Organizations', href: '/orgs' },
        { title: selectedOrganization?.name || '', href: `/orgs/${selectedOrgSlug}` },
        { title: 'Projects', href: `/orgs/${selectedOrgSlug}/projects` },
        {
          title: selectedProject?.name || '',
          href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/home`,
        },
        {
          title: 'Collections',
          href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections`,
        },
        {
          title: collection.name,
          href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${collection.slug}`,
        },
        { title: 'Readme History' },
      ];

      setBreadcrumbs(breadcrumbs);
    }

    setActionButtons([exitButton]);

    return () => {
      setBreadcrumbs([]);
      setActionButtons([]);
    };
  }, [
    exitButton,
    setActionButtons,
    setBreadcrumbs,
    collection,
    selectedOrganization,
    selectedOrgSlug,
    selectedProject,
    selectedProjectSlug,
  ]);

  // Show loader while data is loading
  if (isLoadingCollection || isLoadingVersions) {
    return <Loader />;
  }

  // If collection is loaded but not found, show error
  if (!collection) {
    return <div>Collection not found</div>;
  }

  return (
    <>
      <PageContent>
        <ReadmeHistory
          readmeVersions={readmeVersions}
          onRestoreClick={restoreToPreviousVersion}
          onPreviewClick={previewPreviousVersion}
        />
      </PageContent>

      <VersionPreviewModal
        open={previewModalVisible}
        versionData={selectedVersion}
        onRestoreClick={restoreToPreviousVersion}
        onCancelClick={handleCancelPreview}
      />
    </>
  );
}

export default CollectionReadmeHistory;
